import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/lib/firebase/config';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { signOut } from 'firebase/auth';
import { ArrowRight } from 'lucide-react';
import { LoginPage, LoadingSpinner } from '@elka/ui';
import { setTenantId } from '@elka/firebase';
import { getOrCreateDeviceId, getDeviceName } from '@/lib/device/deviceId';

const RESEND_COOLDOWN = 60;

interface OTPVerificationFormProps {
  confirmationResult: { confirm: (code: string) => Promise<unknown> };
  phoneNumber: string;
  onBack: () => void;
  /** Returns null on success, or a user-facing error message that the OTP screen will surface. */
  onResend: () => Promise<string | null>;
}

interface LinkWorkerResponse {
  workerId: string;
  workerName: string;
  workerType: 'office' | 'field';
  /** Tenant the worker was linked to; present once the server mints tenant claims. */
  tenantId?: string;
}

interface LinkWorkerPayload {
  deviceId: string;
  deviceName: string;
}

export default function OTPVerificationForm({
  confirmationResult,
  phoneNumber,
  onBack,
  onResend,
}: OTPVerificationFormProps) {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCountdown();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startCountdown]);

  const handleResend = async () => {
    setError(null);
    const resendError = await onResend();
    if (resendError) {
      // Don't lock the user out of the resend button — they couldn't actually resend.
      setError(resendError);
      setCountdown(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    startCountdown();
  };
  const navigate = useNavigate();
  const { setWorkerContextAndPersist } = useAuth();

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const [, deviceId] = await Promise.all([
        confirmationResult.confirm(otp),
        getOrCreateDeviceId(),
      ]);
      const deviceName = getDeviceName();
      const linkWorker = httpsCallable<LinkWorkerPayload, LinkWorkerResponse>(
        functions!,
        'teamLinkWorkerByPhone'
      );
      const result = await linkWorker({ deviceId, deviceName });
      const { workerId, workerName, workerType } = result.data;
      // Force token refresh so the new custom claims (workerRole, workerId, tenantId) are
      // picked up immediately — required for Firestore/Storage rules to pass.
      const tokenResult = await auth.currentUser?.getIdTokenResult(true);
      const claimTenantId =
        typeof tokenResult?.claims.tenantId === 'string' && tokenResult.claims.tenantId.length > 0
          ? tokenResult.claims.tenantId
          : result.data.tenantId;
      // Post-cutover: every worker session must carry a tenantId claim. Without
      // one we can't safely scope Firestore/Storage reads, so sign out instead
      // of continuing unset.
      if (!claimTenantId) {
        await signOut(auth);
        setError('החשבון אינו משויך לחברה. יש להתחבר מחדש.');
        return;
      }
      setTenantId(claimTenantId);
      setWorkerContextAndPersist({ workerId, workerName, workerType });
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      const msg = (err as { message?: string })?.message ?? '';
      console.error('[OTP] Login error', { code, msg });
      if (code === 'auth/invalid-verification-code') {
        setError('קוד אימות שגוי. בדוק ונסה שוב.');
      } else if (code === 'auth/code-expired') {
        setError('הקוד פג תוקף. חזור ובקש קוד חדש.');
      } else if (
        msg.includes('Worker not found') ||
        msg.includes('not active') ||
        msg.includes('not-active')
      ) {
        await signOut(auth);
        setError('לא ניתן להתחבר עם מספר זה. פנה למנהל.');
      } else if (
        msg.includes('Workers configuration not found') ||
        msg.includes('Workers not found')
      ) {
        setError('תצורת עובדים חסרה. פנה למנהל המערכת.');
      } else if (
        code === 'functions/failed-precondition' ||
        msg.includes('more than one company') ||
        msg.includes('multiple companies')
      ) {
        // Phone registered under more than one tenant — the server can't pick one for us.
        await signOut(auth);
        setError('מספר הטלפון רשום ביותר מחברה אחת. פנה למנהל המערכת.');
      } else if (msg.includes('phone number') && msg.includes('token')) {
        await signOut(auth);
        setError('שגיאה בנתוני האימות. נסה להתחבר מחדש.');
      } else if (msg.includes('unauthenticated') || msg.includes('Authentication required')) {
        setError('ההתחברות פגה. נסה שוב.');
      } else {
        // In prod, surface error code for debugging (helps diagnose stg vs prod differences)
        setError(
          `שגיאה בכניסה. נסה שוב.${import.meta.env.DEV ? ` [${code}] ${msg}` : ` (קוד: ${code || 'לא ידוע'})`}`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginPage
      calm
      title="הזן קוד אימות"
      subtitle={`נשלח קוד בן 6 ספרות אל ${phoneNumber}`}
      error={error}
      brandTitle="ELKA · פורטל צוות"
      valueProps={['דיווח שעות, סידור עבודה', 'ותלושי שכר —', 'הכל במקום אחד.']}
    >
      <form onSubmit={handleVerifyOTP} className="mt-6 space-y-4">
        <label htmlFor="otp" className="sr-only">
          קוד אימות
        </label>
        <input
          id="otp"
          name="one-time-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          required
          disabled={isLoading}
          autoFocus
          dir="ltr"
          className="rounded-control border-line-strong text-ink bg-surface placeholder:text-sub focus:border-brand w-full border px-4 py-4 text-center text-3xl tracking-[0.5em] tabular-nums transition-colors disabled:opacity-60"
        />
        <p className="text-sub text-center text-xs">הקוד תקף ל-5 דקות</p>

        <button
          type="submit"
          disabled={isLoading || otp.length !== 6}
          className="rounded-control bg-brand hover:bg-brand-ink flex min-h-[52px] w-full items-center justify-center gap-3 py-3.5 text-base font-bold text-white transition-[background-color,transform] active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
        >
          {isLoading ? <LoadingSpinner size="sm" centered={false} /> : 'אמת והתחבר'}
        </button>
      </form>

      <div className="mt-6 flex items-center justify-center gap-4">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="text-muted hover:text-ink flex min-h-10 items-center gap-1.5 px-2 text-sm font-semibold transition-[color,transform] active:scale-[0.96] disabled:opacity-50 disabled:active:scale-100"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה
        </button>
        <span className="text-line-strong">|</span>
        <button
          onClick={handleResend}
          disabled={isLoading || countdown > 0}
          className="text-muted hover:text-ink min-h-10 px-2 text-sm font-semibold transition-[color,transform] active:scale-[0.96] disabled:opacity-50 disabled:active:scale-100"
        >
          <span aria-live="polite" className="tabular-nums">
            {countdown > 0 ? `שלח שוב (${countdown})` : 'לא קיבלת קוד? שלח שוב'}
          </span>
        </button>
      </div>
    </LoginPage>
  );
}
